/**
 * Billing Block Banner
 *
 * STRICT POLICY: Immediate block when payment fails.
 * - Blocked mode: Full-screen overlay forcing payment
 * - No warning mode - payment failure = immediate block
 */

import React from 'react';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBillingAccessFromContext } from '@/contexts/SubscriptionContext';
import { supabase } from '@/integrations/supabase/client';

interface BillingBlockBannerProps {
  className?: string;
}

export function BillingBlockBanner({ className }: BillingBlockBannerProps) {
  // PERF FIX: Use consolidated context instead of separate hook
  const {
    isBlocked,
    isLoading,
    unpaidInvoice,
    daysOverdue,
  } = useBillingAccessFromContext();

  const [isRedirecting, setIsRedirecting] = React.useState(false);

  // Don't show anything if loading or not blocked
  if (isLoading || !isBlocked || !unpaidInvoice) {
    return null;
  }

  const handlePayNow = async () => {
    try {
      setIsRedirecting(true);

      // Call customer-portal function to get Stripe billing portal URL
      const { data: { session } } = await supabase.auth.getSession();

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/customer-portal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            returnUrl: window.location.href,
          }),
        }
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No portal URL returned');
        setIsRedirecting(false);
      }
    } catch (error) {
      console.error('Error redirecting to billing portal:', error);
      setIsRedirecting(false);
    }
  };

  // Format the billing month for display
  const formatBillingMonth = (month: string) => {
    const date = new Date(month);
    const monthNames = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  // BLOCKED: User cannot access the platform - STRICT POLICY
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm ${className}`}>
      <div className="max-w-md p-6 bg-card border border-destructive rounded-lg shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-destructive/10 rounded-full">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <h2 className="text-xl font-semibold text-destructive">
            Accès suspendu
          </h2>
        </div>

        <p className="text-muted-foreground mb-4">
          Votre accès à Subvention360 a été suspendu en raison d&apos;une facture impayée
          pour <strong>{formatBillingMonth(unpaidInvoice.billing_month)}</strong>.
        </p>

        <div className="bg-muted p-3 rounded-md mb-4">
          <p className="text-sm">
            <span className="font-medium">Montant dû :</span>{' '}
            <span className="text-destructive font-semibold">
              {unpaidInvoice.amount?.toFixed(2)} €
            </span>
          </p>
          {daysOverdue > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              En retard de {daysOverdue} jour{daysOverdue > 1 ? 's' : ''}
            </p>
          )}
          {unpaidInvoice.error && (
            <p className="text-sm text-destructive mt-1">
              Erreur : {unpaidInvoice.error}
            </p>
          )}
        </div>

        <Button
          onClick={handlePayNow}
          disabled={isRedirecting}
          className="w-full"
        >
          {isRedirecting ? (
            'Redirection...'
          ) : (
            <>
              <CreditCard className="h-4 w-4 mr-2" />
              Régulariser ma facture
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground mt-3 text-center">
          Vous pourrez payer la facture ou mettre à jour votre moyen de paiement.
          <br />
          Votre accès sera rétabli automatiquement après le paiement.
        </p>
      </div>
    </div>
  );
}
